import React from 'react';
import { Building, Users, GraduationCap, User, Award, TrendingUp, Map } from 'lucide-react';
import type { ReportType } from '@/types/reporting-v2';

interface ReportTypeSelectorProps {
  value: ReportType;
  onChange: (value: ReportType) => void;
  variant?: 'cards' | 'dropdown';
  category?: 'admin' | 'learner' | 'all';
}

const ADMIN_REPORT_TYPES = [
  {
    value: 'organization_overview' as ReportType,
    label: 'Organization Overview',
    description: 'Company-wide training metrics',
    icon: Building,
    category: 'admin' as const,
  },
  {
    value: 'department_performance' as ReportType,
    label: 'Department Performance',
    description: 'Department-level analytics',
    icon: Users,
    category: 'admin' as const,
  },
  {
    value: 'role_analysis' as ReportType,
    label: 'Role Analysis',
    description: 'Performance by job function',
    icon: User,
    category: 'admin' as const,
  },
  {
    value: 'course_effectiveness' as ReportType,
    label: 'Course Effectiveness',
    description: 'Individual course analysis',
    icon: GraduationCap,
    category: 'admin' as const,
  },
];

const LEARNER_REPORT_TYPES = [
  {
    value: 'learner_progress' as ReportType,
    label: 'Users Progress',
    description: 'User learning journey',
    icon: TrendingUp,
    category: 'learner' as const,
  },
  {
    value: 'certifications' as ReportType,
    label: 'Users Certifications',
    description: 'User certification portfolio',
    icon: Award,
    category: 'learner' as const,
  },
  {
    value: 'learning_path' as ReportType,
    label: 'Users Learning Path',
    description: 'User learning path progress',
    icon: Map,
    category: 'learner' as const,
  },
];

export default function ReportTypeSelector({
  value,
  onChange,
  variant = 'cards',
  category = 'all',
}: ReportTypeSelectorProps) {
  const getReportTypes = () => {
    if (category === 'admin') return ADMIN_REPORT_TYPES;
    if (category === 'learner') return LEARNER_REPORT_TYPES;
    return [...ADMIN_REPORT_TYPES, ...LEARNER_REPORT_TYPES];
  };

  const REPORT_TYPES = getReportTypes();

  if (variant === 'dropdown') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ReportType)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {category === 'all' && (
          <>
            <optgroup label="Admin Reports">
              {ADMIN_REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Learner Reports">
              {LEARNER_REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </optgroup>
          </>
        )}
        {category !== 'all' && REPORT_TYPES.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="space-y-6">
      {category === 'all' && (
        <>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Admin Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ADMIN_REPORT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = value === type.value;

                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => onChange(type.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 mb-2 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}
                    />
                    <h4
                      className={`text-sm font-semibold mb-1 ${
                        isSelected ? 'text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      {type.label}
                    </h4>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Learner Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {LEARNER_REPORT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = value === type.value;

                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => onChange(type.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 mb-2 ${isSelected ? 'text-green-600' : 'text-gray-400'}`}
                    />
                    <h4
                      className={`text-sm font-semibold mb-1 ${
                        isSelected ? 'text-green-900' : 'text-gray-900'
                      }`}
                    >
                      {type.label}
                    </h4>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {category !== 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {REPORT_TYPES.map((type) => {
            const Icon = type.icon;
            const isSelected = value === type.value;
            const colorClass = category === 'learner' ? 'green' : 'blue';

            return (
              <button
                key={type.value}
                type="button"
                onClick={() => onChange(type.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? `border-${colorClass}-500 bg-${colorClass}-50`
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <Icon
                  className={`w-6 h-6 mb-2 ${isSelected ? `text-${colorClass}-600` : 'text-gray-400'}`}
                />
                <h4
                  className={`text-sm font-semibold mb-1 ${
                    isSelected ? `text-${colorClass}-900` : 'text-gray-900'
                  }`}
                >
                  {type.label}
                </h4>
                <p className="text-xs text-gray-500">{type.description}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
